import * as React from 'react';
import { connect } from 'react-redux';
import * as Actions from './BloodGlucoseActions';
import { QueryTable, QueryTableColumn } from '../misc/components/queryTable/QueryTable';
import { Authorization } from '../misc/components/Authorization';
import DateRangeModal from '../misc/components/queryTable/DateRangeModal';
import MultiSelectModal from '../misc/components/queryTable/MultiSelectModal';
import { Query, Permission, QueryCondition, Subject, Visit, Site, BloodGlucoseSession } from '../misc/api/apiTypes';
import { MainState } from '../Core/reducers';
import { Dispatch } from 'redux';
import { formatNull, formatDateTime } from '../misc/components/Formatters/Formatters';
import Condition from '../misc/components/Condition';
import uniqBy from 'lodash-es/uniqBy';
import { Link } from 'react-router-dom';
import { useCurrentStudyConfigurationContext } from '../Core/CurrentStudyConfigurationContext';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../misc/api/WebPortalTokenClient';
import { useSecurityContext } from '../Core/SecurityContext';

interface BloodGlucoseQueryTableProps {
    sessionQuery: Query<BloodGlucoseSession>;
    sessionFilterQuery: Query<BloodGlucoseSession>;
    dispatch?: Dispatch<MainState>;
}

function mapStateToProps(state: MainState): BloodGlucoseQueryTableProps {
    return {
        sessionQuery: state.bloodGlucoseList.sessionQuery,
        sessionFilterQuery: state.bloodGlucoseList.sessionFilterQuery,
    };
}

const RenderMenu = (studyId: string, permissions: Array<Permission>, row) => {
    return (
        <Condition test={() => row.StatusId === 'BGSS_RECEIVED_DATA'}>
            <Authorization permissionId="PER_AccessAllBloodGlucoseTests" studyId={studyId} currentPermissions={permissions}>
                <div className="text-center">
                    <Link to={`/bloodglucose/tests/${row.Id}`} type="button" className="btn btn-primary">
                        View Tests
                    </Link>
                </div>
            </Authorization>
        </Condition>
    );
}

const BloodGlucoseQueryTable = (props: BloodGlucoseQueryTableProps) => {
    const { study } = useCurrentStudyConfigurationContext();
    const [showSiteFilter, setShowSiteFilter] = React.useState(false);
    const [showSubjectFilter, setShowSubjectFilter] = React.useState(false);
    const [showVisitFilter, setShowVisitFilter] = React.useState(false);
    const [showSessionFilter, setShowSessionFilter] = React.useState(false);
    const [showConfiguredFilter, setShowConfiguredFilter] = React.useState(false);
    const [showReceivedFilter, setShowReceivedFilter] = React.useState(false);
    const [showConfigurationTechnicianFilter, setShowConfigurationTechnicianFilter] = React.useState(false);
    const [showUploadTechnicianFilter, setShowUploadTechnicianFilter] = React.useState(false);
    const [showSerialNumberFilter, setShowSerialNumberFilter] = React.useState(false);
    const [showStatusFilter, setShowStatusFilter] = React.useState(false);
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();
    const { currentUserPermissions } = useSecurityContext();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);

    React.useEffect(() => {
        const getSites = async () => {
            const sites = await client.client.getSites("id,number", `studyId eq ${study.id}`, "number");
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };

        const getSubjects = async () => {
            const subjects = await client.client.getSubjects("id,number", `site/studyId eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getVisits = async () => {
            const visits = await client.client.getBloodGlucoseSessions("visit", `visit/subject/site/studyId eq ${study.id}`, "visit/name", "visit($expand=subject($expand=site))");
            const options = uniqBy(visits.value, o => o.visit.name).map(s => ({
                value: s.visit.name,
                label: s.visit.name,
            }));

            setVisitOptions(options);
        };

        props.dispatch(Actions.FetchSessionFilterAsyncAction.create(legacyClient));
        props.dispatch(Actions.FetchBloodGlucoseSessionsAction.create(legacyClient));

        getSites();
        getSubjects();
        getVisits();
    }, []);

    const { sessionQuery, dispatch } = props;

    const handlePage = (page: number) => {
        dispatch(Actions.PageBloodGlucoseSessionsAction.create(page));
        dispatch(Actions.FetchBloodGlucoseSessionsAction.create(legacyClient));
    };
    const handleSort = member => {
        dispatch(Actions.SortBloodGlucoseSessionsAction.create(member));
        dispatch(Actions.FetchBloodGlucoseSessionsAction.create(legacyClient));
    };
    const handleFilter = member => {
        switch (member) {
            case 'Site.SiteNumber':
                setShowSiteFilter(true);
                break;
            case 'Subject.SubjectNumber':
                setShowSubjectFilter(true);
                break;
            case 'Visit.Name':
                setShowVisitFilter(true);
                break;
            case 'Name':
                setShowSessionFilter(true);
                break;
            case 'ConfigurationTechnician':
                setShowConfigurationTechnicianFilter(true);
                break;
            case 'UploadTechnician':
                setShowUploadTechnicianFilter(true);
                break;
            case 'DeviceSerialNumber':
                setShowSerialNumberFilter(true);
                break;
            case 'ConfigurationDateTime':
                setShowConfiguredFilter(true);
                break;
            case 'ReceivedDateTime':
                setShowReceivedFilter(true);
                break;
            case 'Status':
                setShowStatusFilter(true);
                break;
        }
    };
    const handleMultiSelectFilterChange = (member: string, values: string[]) => dispatch(Actions.FilterBloodGlucoseSessionsAction.create(member, QueryCondition.Equal, values));
    const handleDateRangeFilterChange = (member: string, from: string, to: string) => {
        dispatch(Actions.FilterBloodGlucoseSessionsAction.create(member, QueryCondition.GreaterThanOrEqual, from));
        dispatch(Actions.FilterBloodGlucoseSessionsAction.create(member, QueryCondition.LessThanOrEqual, to));
    }
    const handleSiteFilterChange = (values) => handleMultiSelectFilterChange('Site.SiteNumber', values.map(v => v.label));
    const handleSubjectFilterChange = (values) => handleMultiSelectFilterChange('Subject.SubjectNumber', values.map(v => v.label));
    const handleVisitFilterChange = (values) => handleMultiSelectFilterChange('Visit.Name', values.map(v => v.label));
    const handleSessionFilterChange = (values) => handleMultiSelectFilterChange('Name', values.map(v => v.Name));
    const handleConfigurationTechnicianFilterChange = (values) => handleMultiSelectFilterChange('ConfigurationTechnician', values.map(v => v.ConfigurationTechnician));
    const handleUploadTechnicianFilterChange = (values) => handleMultiSelectFilterChange('UploadTechnician', values.map(v => v.UploadTechnician));
    const handleSerialNumberFilterChange = (values) => handleMultiSelectFilterChange('DeviceSerialNumber', values.map(v => v.DeviceSerialNumber));
    const handleConfiguredFilterChange = (from: string, to: string): void => handleDateRangeFilterChange('ConfigurationDateTime', from, to);
    const handleReceivedFilterChange = (from: string, to: string): void => handleDateRangeFilterChange('ReceivedDateTime', from, to);
    const handleStatusFilterChange = (values) => handleMultiSelectFilterChange('Status', values.map(v => v.Status));

    const handleFilterClose = () => {
        setShowSiteFilter(false);
        setShowSubjectFilter(false);
        setShowVisitFilter(false);
        setShowSessionFilter(false);
        setShowConfigurationTechnicianFilter(false);
        setShowUploadTechnicianFilter(false);
        setShowSerialNumberFilter(false);
        setShowConfiguredFilter(false);
        setShowReceivedFilter(false);
        setShowStatusFilter(false);

        dispatch(Actions.FetchBloodGlucoseSessionsAction.create(legacyClient));
    }
    const getFilterValues = (member: string, condition: QueryCondition): any => sessionQuery
        && sessionQuery.Filters
        && sessionQuery.Filters.find(item => item.Member === member && item.Condition === condition)
        && sessionQuery.Filters.find(item => item.Member === member && item.Condition === condition).Values;

    const getFilterValue = (member: string, condition: QueryCondition): any => getFilterValues(member, condition)
        && getFilterValues(member, condition).find(() => true);

    return <>
        <QueryTable query={sessionQuery} rowUniqueIdentifier={(row) => row.Id} onPage={handlePage} onSort={handleSort} onFilter={handleFilter}>
            <QueryTableColumn member='Site.SiteNumber' header='Site' map={(row) => row.Site.SiteNumber} sortable={true} filterable={true} />
            <QueryTableColumn member='Subject.SubjectNumber' header='Subject' map={(row) => row.Subject.SubjectNumber} sortable={true} filterable={true} />
            <QueryTableColumn member='Visit.Name' header='Visit' map={(row) => formatNull(row.Visit.Name)} sortable={true} filterable={true} />
            <QueryTableColumn member='Name' header='Session' map={(row) => formatNull(row.Name)} sortable={true} filterable={true} />
            <QueryTableColumn member='ConfigurationTechnician' header='Configuration Technician' map={(row) => formatNull(row.ConfigurationTechnician)} sortable={true} filterable={true} />
            <QueryTableColumn member='UploadTechnician' header='Upload Technician' map={(row) => formatNull(row.UploadTechnician)} sortable={true} filterable={true} />
            <QueryTableColumn member='DeviceSerialNumber' header='Serial Number' map={(row) => row.DeviceSerialNumber} sortable={true} filterable={true} />
            <QueryTableColumn member='ConfigurationDateTime' header='Session Configured' map={(row) => formatDateTime(row.ConfigurationDateTime)} sortable={true} filterable={true} />
            <QueryTableColumn member='ReceivedDateTime' header='Session Received' map={(row) => formatDateTime(row.ReceivedDateTime)} sortable={true} filterable={true} />
            <QueryTableColumn member='Status' header='Status' map={(row) => row.Status} sortable={true} filterable={true} />
            <QueryTableColumn member='' header='' map={(row) => RenderMenu(study.id, currentUserPermissions, row)} />
        </QueryTable>
        <MultiSelectModal header='Site Filter' show={showSiteFilter} options={siteOptions} valueKey={'label'} labelKey={'label'} values={getFilterValues('Site.SiteNumber', QueryCondition.Equal)} onChange={handleSiteFilterChange} onApply={handleFilterClose} onClose={handleFilterClose} />
        <MultiSelectModal header='Subject Filter' show={showSubjectFilter} options={subjectOptions} valueKey={'label'} labelKey={'label'} values={getFilterValues('Subject.SubjectNumber', QueryCondition.Equal)} onChange={handleSubjectFilterChange} onApply={handleFilterClose} onClose={handleFilterClose} />
        <MultiSelectModal header='Visit Filter' show={showVisitFilter} options={visitOptions} valueKey={'label'} labelKey={'label'} values={getFilterValues('Visit.Name', QueryCondition.Equal)} onChange={handleVisitFilterChange} onApply={handleFilterClose} onClose={handleFilterClose} />
        <MultiSelectModal header='Session Filter' show={showSessionFilter} options={props.sessionFilterQuery ? uniqBy(props.sessionFilterQuery.Data, x => x.Name) : []} valueKey={'Name'} labelKey={'Name'} values={getFilterValues('Name', QueryCondition.Equal)} onChange={handleSessionFilterChange} onApply={handleFilterClose} onClose={handleFilterClose} />
        <MultiSelectModal header='Configuration Technician Filter' show={showConfigurationTechnicianFilter} options={props.sessionFilterQuery ? uniqBy(props.sessionFilterQuery.Data, x => x.ConfigurationTechnician) : []} valueKey={'ConfigurationTechnician'} labelKey={'ConfigurationTechnician'} values={getFilterValues('ConfigurationTechnician', QueryCondition.Equal)} onChange={handleConfigurationTechnicianFilterChange} onApply={handleFilterClose} onClose={handleFilterClose} />
        <MultiSelectModal header='Upload Technician Filter' show={showUploadTechnicianFilter} options={props.sessionFilterQuery ? uniqBy(props.sessionFilterQuery.Data, x => x.UploadTechnician) : []} valueKey={'UploadTechnician'} labelKey={'UploadTechnician'} values={getFilterValues('UploadTechnician', QueryCondition.Equal)} onChange={handleUploadTechnicianFilterChange} onApply={handleFilterClose} onClose={handleFilterClose} />
        <MultiSelectModal header='Serial Number Filter' show={showSerialNumberFilter} options={props.sessionFilterQuery ? uniqBy(props.sessionFilterQuery.Data, x => x.DeviceSerialNumber) : []} valueKey={'DeviceSerialNumber'} labelKey={'DeviceSerialNumber'} values={getFilterValues('DeviceSerialNumber', QueryCondition.Equal)} onChange={handleSerialNumberFilterChange} onApply={handleFilterClose} onClose={handleFilterClose} />
        <DateRangeModal header='Session Configured Filter' show={showConfiguredFilter} to={getFilterValue('ConfigurationDateTime', QueryCondition.LessThanOrEqual)} from={getFilterValue('ConfigurationDateTime', QueryCondition.GreaterThanOrEqual)} onChange={handleConfiguredFilterChange} onApply={handleFilterClose} onClose={handleFilterClose} />
        <DateRangeModal header='Session Received Filter' show={showReceivedFilter} to={getFilterValue('ReceivedDateTime', QueryCondition.LessThanOrEqual)} from={getFilterValue('ReceivedDateTime', QueryCondition.GreaterThanOrEqual)} onChange={handleReceivedFilterChange} onApply={handleFilterClose} onClose={handleFilterClose} />
        <MultiSelectModal header='Status Filter' show={showStatusFilter} options={props.sessionFilterQuery ? uniqBy(props.sessionFilterQuery.Data, x => x.Status) : []} valueKey={'Status'} labelKey={'Status'} values={getFilterValues('Status', QueryCondition.Equal)} onChange={handleStatusFilterChange} onApply={handleFilterClose} onClose={handleFilterClose} />
    </>;
}

export default connect<BloodGlucoseQueryTableProps>(mapStateToProps)(BloodGlucoseQueryTable);
